import React, {useState} from 'react';
import {Button} from '@material-ui/core';
import CheckIcon from 'components/CheckIcon/CheckIcon';
import {Route} from 'react-router-dom';
import {IFdFarm, ISupplierUserProfile} from 'constants/interfaces';
import {FdFarmForm} from 'components/applications/FdFarmForm';
import {
  reduxToggleSidePanel,
  reduxSetSidePanelEditState,
} from 'appredux/actions';
import {apiUpdateFarm} from 'constants/Api';

const AddOrUpdateFdFarm = (props: {
  user: ISupplierUserProfile;
  farm?: IFdFarm;
}) => {
  const [farm, setFarm] = useState(props.farm);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSuccessfullyCreatedFarm, setHasSuccessfullyCreatedFarm] =
    useState(false);

  const isAdding = !props.farm;
  const canWrite = true;

  /* ---------- submit ---------- */
  const handleSubmit = (
    f: Partial<IFdFarm> & {fullTimeFarming?: boolean}, // ← updated
  ) => {
    if (!canWrite) return;
    setIsLoading(true);

    /* convert leaseEndDate (string) ➜ Unix-seconds Float */
    let leaseEndDateFloat: number | undefined;
    if (typeof f.leaseEndDate === 'string' && f.leaseEndDate !== '') {
      leaseEndDateFloat = Math.floor(Date.parse(f.leaseEndDate) / 1000);
    } else if (typeof f.leaseEndDate === 'number') {
      leaseEndDateFloat = f.leaseEndDate;
    }

    /* build DTO – cast to any so TS tolerates extra props */
    const farmInput: any = {
      farmId: parseInt(f.id!, 10),
      name: f.name,
      description: f.description,
      numberOfLeasedHectares: f.numberOfLeasedHectares,
      ownedHectares: f.ownedHectares,
      irrigatedHectares: f.irrigatedHectares,
      drylandHectares: f.drylandHectares,
      isLeased: f.isLeased,
      leaseEndDate: leaseEndDateFloat,
      waterRights: f.waterRights,
      fullTimeFarming: f.fullTimeFarming,
      shortTermGoals: f.shortTermGoals,
      longTermGoals: f.longTermGoals,
      landComposition: f.landComposition,
      workforceNumber: f.workforceNumber,
      workerTrust: f.workerTrust,
      employmentBeforeAndAfter: f.employmentBeforeAndAfter,
    };

    apiUpdateFarm(farmInput)
      .then(() => {
        setHasSuccessfullyCreatedFarm(true);
        reduxSetSidePanelEditState('saved');
      })
      .catch((err) => {
        alert('Failed to save farm at the moment. Please try again later');
        console.error('Failed to save farm', err);
      })
      .finally(() => setIsLoading(false));
  };

  const handleCancel = () => reduxToggleSidePanel(false, null);

  /* ---------- success splash ---------- */
  if (hasSuccessfullyCreatedFarm) {
    return (
      <Route
        render={() => (
          <div className="side-panel-success-container">
            <div className="mid-section">
              <div className="success-gif">
                <CheckIcon />
              </div>
              <div className="success-msg">
                {isAdding
                  ? 'Farm added successfully'
                  : 'Farm updated successfully'}
              </div>
            </div>
            <div className="btn-container">
              {isAdding && (
                <Button
                  className="primary-btn alt-button"
                  variant="outlined"
                  disabled={isLoading}
                  onClick={() => {
                    setFarm(undefined);
                    setHasSuccessfullyCreatedFarm(false);
                  }}
                >
                  Add another farm
                </Button>
              )}
              <Button
                className="primary-btn"
                variant="outlined"
                color="primary"
                disabled={isLoading}
                onClick={() => reduxToggleSidePanel(false, null)}
              >
                Done
              </Button>
            </div>
          </div>
        )}
      />
    );
  }

  /* ---------- form ---------- */
  return (
    <FdFarmForm
      farm={farm}
      isLoading={isLoading}
      submitButtonTitle={isAdding ? 'Add' : 'Update'}
      onSubmit={handleSubmit}
      cancelButtonTitle="Cancel"
      onCancel={handleCancel}
      readonly={!canWrite}
    />
  );
};

export default AddOrUpdateFdFarm;
